import { Suspense, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Loading from "../../../../common/components/ui/loading/Loading";
import { formatDateAndTime } from "../../../../common/helpers/date.helpers";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import GenericTable from "../../../../common/components/ui/table/GenericTable";
import {
  TableFilters,
  TableFiltersState,
} from "../../../../common/components/ui/table/TableFilters";
import TableFooter from "../../../../common/components/ui/table/TableFooter";
import ModalCreateRenter from "../modals/CreateRenter";
import ModalEditRenter from "../modals/EditRenter";
import ModalApproveRenter from "../modals/ApproveRenter";
import { getRenters } from "../../../../common/services/renters.api";
import {
  IRenter,
  RENTER_STATUS,
  RenterType,
  FINANCIAL_STATUS,
} from "../../../../types/renter.type";
import Pendencies from "../Pendencies";
import { ModalReservePayment } from "../modals/fields/ReservePayment";
import ModalConvertRenter from "../modals/ModalConvertRenter";
import ModalChangeStatus from "../modals/ChangeStatusRenter";
import ModalApproveRenterMotoClub from "../modals/ApproveRenterMotoClub";
import ModalEditRenterMotoclub from "../modals/EditRenterMotoclub";
import ModalEditRenterSupportJson from "../modals/EditRenterSupportJson";

import { checkIsAmbassador } from "../../../../common/services/ambassador";
import { AuthContext } from "../../../../common/components/auth/authContext/AuthContext";
import "./TableRenter.scss";
import { differenceInDays } from "date-fns";

import { useCompanyStore } from "../../../../common/states/store/Company";

import CommercialActions from "../modals/CommercialActionsRenter";
import { getCompanySigla } from "@/common/helpers/company.helpers";
import { ConsultantOption } from "@/types/employee.type";
import { getConsultantOptionsFromAPI } from "@/common/services/employee.api";

// eslint-disable-next-line react-refresh/only-export-components
export const STATUS_LABELS = {
  [RENTER_STATUS.Ativo]: "Ativo",
  [RENTER_STATUS.Congelado]: "Congelado",
  [RENTER_STATUS.ReservaPaga]: "Reserva paga",
  [RENTER_STATUS.PendentePagamento]: "Pendente de pagamento",
  [RENTER_STATUS.ProntoParaAprovacao]: "Pronto para aprovação",
  [RENTER_STATUS.ReservaPendentePagamento]: "Reserva pendente de pagamento",
  [RENTER_STATUS.Blacklist]: "Blacklist",
};

// eslint-disable-next-line react-refresh/only-export-components
export const SITUATION_FINANCIAL_LABELS = {
  [FINANCIAL_STATUS.INADIMPLENTE]: "Inadimplente",
  [FINANCIAL_STATUS.ADIMPLENTE]: "Adimplente",
};

// eslint-disable-next-line react-refresh/only-export-components
export function getStatusLabelsRenters(status: RENTER_STATUS) {
  return STATUS_LABELS[status] || status;
}

const EMPTY_ACTION = { eventName: "", data: undefined };

type paymentIndication = {
  status: boolean;
  file: string;
};

function TableRenters() {
  const { auth } = useContext(AuthContext);

  const { selectedCompanies, selectedRegion, selectedUf, filterMode } =
    useCompanyStore();

  const navigate = useNavigate();
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [renters, setRenters] = useState<IRenter[]>([]);
  const [isAmbassador, setIsAmbassador] = useState(false);
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-updatedAt",
  });
  const [consultantOptions, setConsultantOptions] = useState<
    ConsultantOption[]
  >([]);
  // const currentUF = useCompanyStore((state) => state.selectedUf)

  useEffect(() => {
    async function checkAmbassador() {
      if (auth && auth.user) {
        const ambassador = await checkIsAmbassador(auth);
        setIsAmbassador(ambassador);
      }
    }

    checkAmbassador();
  }, [auth]);

  useEffect(() => {
    async function loadRenters(filters, page) {
      setIsLoading(true);
      setRenters([]);
      const filtersCopy = { ...filters };
      if (filtersCopy.status.length === 0) {
        filtersCopy.status = [
          RENTER_STATUS.Ativo,
          RENTER_STATUS.ProntoParaAprovacao,
          RENTER_STATUS.PendentePagamento,
          RENTER_STATUS.ReservaPaga,
          RENTER_STATUS.ReservaPendentePagamento,
          RENTER_STATUS.Congelado,
          RENTER_STATUS.Indefinido,
          RENTER_STATUS.Incompleto,
        ].join(",");
      }

      const result = await getRenters({ ...filtersCopy, page });

      // Validação do resultado
      if (!result || !result.ok || !result.data) {
        setData({
          itemsPerPage: data.itemsPerPage,
          totalItems: 0,
        });
        setRenters([]);
        setIsLoading(false);
        notifyError(
          (result as { message?: string })?.message || "Erro ao carregar clientes"
        );
        return;
      }

      const rentersWithRescision = result.data.map((renterData) => {
        let rescisionDaysLeft;
        const vehicleHistory = renterData?.desactivationData?.vehicleHistory;
        if (vehicleHistory && vehicleHistory.length > 0) {
          const cancelDate =
            vehicleHistory[vehicleHistory.length - 1].vehicleHistoryEnd;

          rescisionDaysLeft =
            differenceInDays(new Date(), new Date(cancelDate)) - 10;
        }
        return { ...renterData, rescisionDaysLeft };
      });

      setRenters(rentersWithRescision);
      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems || 0,
      });
      setIsLoading(false);
    }
    loadRenters(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, filterMode, selectedCompanies, selectedRegion, selectedUf]);

  const actions = [
    {
      label: "Editar",
      action: (data) =>
        setAction({ eventName: "edit", data: (data as RenterType)._id }),
      permission: "edit:renters",
      // disabled: (data) => ![
      //   RENTER_STATUS.PendentePagamento,
      //   RENTER_STATUS.ProntoParaAprovacao,
      //   RENTER_STATUS.Incompleto,
      //   RENTER_STATUS.Indefinido,
      //   RENTER_STATUS.LeadQualificado,
      //   RENTER_STATUS.Ativo
      // ].includes((data as RenterType).status as RENTER_STATUS)
    },
    {
      label: "Editar JSON (Suporte)",
      action: (data) =>
        setAction({
          eventName: "supportEdit",
          data: (data as RenterType)._id,
        }),
      permission: "renters:edit-support",
    },

    {
      label: "Aprovar cadastro",
      action: (data) => setAction({ eventName: "approve", data: data }),
      disabled: (data) =>
        RENTER_STATUS.ProntoParaAprovacao !== (data as RenterType).status,
      permission: "approve:renters",
    },
    {
      label: "Gerar reserva",
      action: (data) => setAction({ eventName: "reserve", data: data }),
      disabled: (data) =>
        RENTER_STATUS.ProntoParaAprovacao !== (data as RenterType).status,
      permission: "reserve:renters",
    },
    {
      label: "Converter em lead",
      action: (data) => setAction({ eventName: "convert", data: data }),
      disabled: (data) =>
        RENTER_STATUS.ProntoParaAprovacao !== (data as RenterType).status,
      permission: "convertLead:renters",
    },
    {
      label: "Trocar status",
      action: (data) => setAction({ eventName: "changeStatus", data: data }),
      disabled: (data) =>
        [RENTER_STATUS.Ativo].includes(
          (data as RenterType).status as RENTER_STATUS
        ),
      permission: "changeSatus:renters",
      visible: () => auth?.user.permissions.includes("changeSatus:renters") ?? false,

    },
    {
      label: "Editar (Motoclub)",
      action: (data) =>
        setAction({
          eventName: "editMotoClub",
          data: (data as RenterType)._id,
        }),
      permission: "editMotoclub:renters",
      visible: () => auth?.user.roles.includes("motoclub-solidy") ?? false,
    },
    {
      label: "Alugar Moto (Moto Club)",
      action: (data) => setAction({ eventName: "approveMotoClub", data: data }),
      disabled: (data) =>
        RENTER_STATUS.ProntoParaAprovacao !== (data as RenterType).status &&
        RENTER_STATUS.ReservaPaga !== (data as RenterType).status,
      permission: "approveMotoclub:motoClub",
      visible: () => auth?.user.roles.includes("motoclub-solidy") ?? false,
    },
    {
      label: "Ações Comerciais",
      action: (data) =>
        setAction({
          eventName: "commercialActions",
          data: {
            auth, // Inclui o auth completo
            renterId: (data as RenterType)._id, // Inclui o ID do locatário
          },
        }),
      disabled: () => !auth?.user.roles.includes("commercial-leader"),
      visible: () => auth?.user.roles.includes("commercial-leader") ?? false,
      permission: "commercialActions:renters",
    },
  ];

  const isAction = (eventName: string) => action.eventName === eventName;

  const cleanActionData = () => {
    handleSearch(filters);
    setAction(EMPTY_ACTION);
  };

  const handleSearch = ({ search, status }) => {
    const newState = Object.assign({}, filters, { search, status });
    setFilters(newState);
  };

  function onRenterCreatedMotoclub(renterId: string) {
    handleSearch(filters);
    setAction({
      eventName: "editMotoClub",
      data: renterId,
    });
  }

  let headers = {};
  if (isAmbassador) {
    headers = {
      companyId: "Cidade",
      name: "Nome",
      technicalConsultant: "Consultor",
      pendencies: "Pendências",
      status: "Status",
      leadOrigin: "Origem",
      paymentIndication: "Pagamento",
      updatedAt: "Atualizado em",
      createdAt: "Criado em",
    };
  } else {
    headers = {
      companyId: "Cidade",
      name: "Nome",
      technicalConsultant: "Consultor",
      pendencies: "Pendências",
      status: "Status",
      leadOrigin: "Origem",
      plan: "Plano",
      updatedAt: "Atualizado em",
      createdAt: "Criado em",
      // actions: 'Ações'
    };
  }

  useEffect(() => {
    const fetchConsultants = async () => {
      const options = await getConsultantOptionsFromAPI();
      setConsultantOptions(options);
    };

    fetchConsultants();
  }, [filterMode, selectedCompanies, selectedRegion, selectedUf]);

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };
  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Clientes ativos"
          placeholder="Digite o nome, o e-mail, o CPF ou a CNH."
          statusOptions={STATUS_LABELS}
          situationOptions={SITUATION_FINANCIAL_LABELS}
          newItemLabel="Novo Cliente"
          onNewItemClick={() =>
            setAction({ eventName: "create", data: undefined })
          }
          onSearch={handleFilterChange}
          entity="renters"
          permission="createRenterMotoclub:motoClub"
          technicalConsultant={consultantOptions}
          tableData={renters}
        />

        <GenericTable
          headers={headers}
          data={renters}
          actions={actions}
          setFilters={({ orderBy }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              orderBy,
            }));
          }}
          renders={
            {
              companyId: (companyId) => {
                return getCompanySigla(companyId) || "";
              },
              name: (name, renter) =>
                isAmbassador ? (
                  <span>{name}</span>
                ) : (
                  <a
                    href={`/clientes/${(renter as IRenter)._id}`}
                    onClick={(e) => {
                      e.preventDefault(); // Para evitar o comportamento padrão do link
                      navigate(`/clientes/${(renter as IRenter)._id}`); // Navegação na mesma página
                    }}
                    target="_blank" // Abre em uma nova aba
                    rel="noopener noreferrer" // Melhoria de segurança
                  >
                    {name as string}
                  </a>
                ),
              technicalConsultant: (consultant) => {
                // Proteção para objetos vazios
                if (typeof consultant === 'string') {
                  return consultant || '-';
                }
                if (consultant && typeof consultant === 'object') {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  return (consultant as any).name || '-';
                }
                return '-';
              },
              plan: (_, renter) => {
                const label = renter?.activationData?.plan?.label || "-";
                return label.split("(")[0].trim();
              },
              paymentIndication: (paymentIndication: paymentIndication) => {
                // Proteção para undefined/null
                if (!paymentIndication || typeof paymentIndication !== 'object') {
                  return <span className="font-semibold text-gray-400">-</span>;
                }

                return paymentIndication.status ? (
                  paymentIndication.file !== null ? (
                    <a
                      href={`https://assets.locavibe.com.br/${paymentIndication.file}`}
                      className="font-semibold text-green"
                      target="_blank"
                    >
                      Pago
                    </a>
                  ) : (
                    <span className="font-semibold text-green">Pago</span>
                  )
                ) : (
                  <span className="font-semibold text-red">Não Pago</span>
                );
              },
              pendencies: (_, renter) => (
                <Pendencies renter={renter as RenterType} />
              ),
              updatedAt: formatDateAndTime,
              createdAt: formatDateAndTime,
              status: getStatusLabelsRenters,
              leadOrigin: (leadOrigin) => leadOrigin || "-",
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          }
          entity="renters"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />

        {isAction("create") && (
          <ModalCreateRenter
            onClose={cleanActionData}
            onSave={onRenterCreatedMotoclub}
          />
        )}
        {isAction("edit") && (
          <ModalEditRenter
            renterId={action.data as string}
            onClose={cleanActionData}
          />
        )}
        {isAction("supportEdit") && (
          <ModalEditRenterSupportJson
            renterId={action.data as string}
            onClose={cleanActionData}
          />
        )}
        {isAction("editMotoClub") && (
          <ModalEditRenterMotoclub
            renterId={action.data as string}
            onClose={cleanActionData}
          />
        )}
        {isAction("approve") && (
          <ModalApproveRenter
            renter={action.data as { _id: string; name: string }}
            onClose={cleanActionData}
          />
        )}
        {isAction("reserve") && (
          <ModalReservePayment
            renter={action.data as { _id: string; name: string }}
            onClose={cleanActionData}
          />
        )}
        {isAction("changeStatus") && (
          <ModalChangeStatus
            renter={action.data as IRenter}
            onClose={cleanActionData}
          />
        )}
        {isAction("commercialActions") && (
          <CommercialActions
            data={action.data as { auth: string; renterId: string }}
            onClose={cleanActionData}
          />
        )}
        {isAction("convert") && (
          <ModalConvertRenter
            renter={action.data as { _id: string; name: string }}
            onClose={cleanActionData}
          />
        )}
        {isAction("approveMotoClub") && (
          <ModalApproveRenterMotoClub
            renter={action.data as { _id: string; name: string }}
            onClose={cleanActionData}
          />
        )}
      </div>
    </Suspense>
  );
}

export default TableRenters;
